import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../utils/api';
import PhotoGallery from './PhotoGallery';
import { CandidateSafety, SafetyBanner } from './TaxonSafety';

/*
 * Reference and literature data for a taxon, to confirm or reject an
 * identification against: fruiting-body photographs already held, macroscopic
 * traits, spore size / shape / Q / reaction, the dictionary's taxonomy and
 * notes, distribution, and every source cited.
 *
 * ReferenceProfile / ReferencePhotos / Sources were moved here from
 * AiIdentifyPanel.js, which still renders them per candidate. TaxonDiagnosticPanel
 * is the stand-alone form (GET /taxonomy/profile) used where there is a name but
 * no identification result, such as the Identify game's reveal.
 *
 * Empty sections say they are empty. No trait or spore literature has been
 * imported for most taxa yet, and "no spore data" must not read as "no spores".
 * Edibility is never shown: the API never sends it.
 */

export function collectSourceIds(obj, found = new Set()) {
  if (Array.isArray(obj)) {
    obj.forEach(v => collectSourceIds(v, found));
  } else if (obj && typeof obj === 'object') {
    Object.entries(obj).forEach(([k, v]) => {
      if (k === 'source_id' && typeof v === 'string') found.add(v);
      else if (k === 'source_ids' && Array.isArray(v)) v.forEach(x => found.add(x));
      else collectSourceIds(v, found);
    });
  }
  return found;
}

export function Sources({ ids, sources, t }) {
  const known = [...ids].map(id => [id, sources?.[id]]).filter(([, s]) => s);
  if (!known.length) return null;
  return (
    <details style={styles.details}>
      <summary style={styles.summary} title={t('tip.sources')}>{t('safety.sources')} ({known.length})</summary>
      <ol style={styles.sourceList}>
        {known.map(([id, s]) => (
          <li key={id} style={styles.source} data-testid="ai-source">
            {s.citation}
            {s.url && (
              <> {' '}<a href={s.url} target="_blank" rel="noopener noreferrer" title={t('tip.sourceLink')}>{s.doi || s.url}</a></>
            )}
            {s.retraction_status && !['none', 'unchecked'].includes(s.retraction_status) && (
              <strong style={styles.retraction}> [{t(`safety.retraction.${s.retraction_status}`)}]</strong>
            )}
          </li>
        ))}
      </ol>
    </details>
  );
}

const range = (a, b) => (a != null && b != null ? `${a}–${b}` : (a ?? b ?? '?'));
const counts = obj => Object.entries(obj || {})
  .sort((a, b) => b[1] - a[1])
  .map(([k, v]) => `${k} (${v})`)
  .join(', ');

export function ReferenceProfile({ p, t, open = false, showEmpty = false }) {
  const ref = p.reference;
  const internal = p.internal_observations;
  const consistency = p.consistency;
  const dictionary = p.dictionary;
  if (!ref && !internal && !p.trophic_mode && !dictionary) {
    return <p style={styles.refNone}>{t('reference.none')}</p>;
  }
  const dist = ref?.distribution;
  const phen = ref?.phenology?.months || {};
  const countryScope = Object.keys(phen).find(k => k.startsWith('country:'));
  const months = countryScope ? phen[countryScope] : phen.global;
  const traitEntries = Object.entries(ref?.traits || {});
  const microscopy = ref?.microscopy || [];
  const trophic = p.trophic_mode || dictionary?.trophic_mode;
  const growth = p.growth_form || dictionary?.growth_form;
  const lineage = dictionary && ['phylum', 'class_name', 'order_name', 'family', 'genus']
    .map(k => dictionary[k]).filter(Boolean);

  return (
    <details style={styles.details} data-testid="ai-reference" open={open || undefined}>
      <summary style={styles.summary} title={t('tip.reference')}>{t('reference.title')}</summary>
      <div style={styles.refBody}>
        {consistency && consistency.checked > 0 && (
          <div style={consistency.contradicted ? styles.contradicted : styles.consistent}
               data-testid="ai-consistency">
            <strong>{t('reference.consistency')}</strong>
            <div>{t('reference.consistent', { consistent: consistency.consistent,
                                                checked: consistency.checked })}</div>
            {consistency.contradictions.map(c => (
              <div key={c.character}>
                {t('reference.contradiction', {
                  character: t(`reference.category.${c.character}`),
                  observed: c.observed,
                  reference: c.reference.join(' / ') || '—',
                })}
              </div>
            ))}
          </div>
        )}
        {ref?.accepted_name && <div>{t('reference.accepted', { name: ref.accepted_name })}</div>}
        {(ref?.family || dictionary?.family) && (
          <div>{t('reference.family', { name: ref?.family || dictionary.family })}</div>
        )}
        {lineage && lineage.length > 0 && (
          <div data-testid="ai-lineage">{t('reference.lineage', { lineage: lineage.join(' › ') })}</div>
        )}
        {dictionary?.hebrew_name && <div>{t('reference.hebrewName', { name: dictionary.hebrew_name })}</div>}
        {trophic && <div>{t('reference.trophicMode', { value: trophic })}</div>}
        {growth && <div>{t('reference.growthForm', { value: growth })}</div>}
        {ref?.vernacular && (ref.vernacular.heb?.length > 0 || ref.vernacular.eng?.length > 0) && (
          <div>{t('reference.vernacular')}: {[...(ref.vernacular.heb || []),
                                               ...(ref.vernacular.eng || [])].join(', ')}</div>
        )}
        {dictionary?.notes && (
          <div style={styles.refSection} data-testid="ai-taxonomy-notes">
            <strong>{t('reference.notes')}</strong>
            <div style={{ whiteSpace: 'pre-wrap' }}>{dictionary.notes}</div>
          </div>
        )}

        {traitEntries.length > 0 ? (
          <div style={styles.refSection}>
            <strong>{t('reference.traits')}</strong>
            {traitEntries.map(([cat, values]) => (
              <div key={cat}>
                {t(`reference.category.${cat}`)}: {values.map(v => (
                  v.state === 'typical' || v.state === 'reported'
                    ? v.value : `${v.value} (${v.state})`)).join(', ')}
              </div>
            ))}
          </div>
        ) : showEmpty && <div style={styles.refEmpty} data-testid="ai-no-traits">{t('reference.noTraits')}</div>}

        {microscopy.length > 0 ? (
          <div style={styles.refSection} data-testid="ai-microscopy">
            <strong>{t('reference.microscopy')}</strong>
            {microscopy.map((m, i) => (
              <div key={i}>
                {t('reference.spores', { length: range(...m.spore_length_um),
                                         width: range(...m.spore_width_um) })}
                {m.q && m.q[0] != null && <>; {t('reference.q', { q: range(...m.q) })}</>}
                {m.spore_shape && <>; {t('reference.sporeShape', { shape: m.spore_shape })}</>}
                {m.spore_reaction && <>; {m.spore_reaction}</>}
                {m.ornamentation && <>; {m.ornamentation}</>}
              </div>
            ))}
          </div>
        ) : showEmpty && <div style={styles.refEmpty} data-testid="ai-no-microscopy">{t('reference.noMicroscopy')}</div>}

        {dist && (dist.occurrence_total != null || dist.top_countries?.length > 0) && (
          <div style={styles.refSection}>
            <strong>{t('reference.distribution')}</strong>
            {dist.country_code && dist.recorded_in_country != null && (
              <div data-testid="ai-recorded-here">
                {t(dist.recorded_in_country ? 'reference.recordedHere' : 'reference.notRecordedHere',
                   { country: dist.country_code })}
              </div>
            )}
            {dist.occurrence_total != null && (
              <div>{t('reference.occurrences', { n: dist.occurrence_total })}</div>
            )}
            {dist.top_countries?.length > 0 && (
              <div>{t('reference.topCountries', {
                countries: dist.top_countries.map(c => `${c.country_code} (${c.occurrences})`).join(', '),
              })}</div>
            )}
          </div>
        )}

        {months && Object.keys(months).length > 0 && (
          <div style={styles.refSection}>
            <strong>{countryScope
              ? t('reference.phenologyCountry', { country: countryScope.slice(8) })
              : t('reference.phenologyGlobal')}</strong>
            <div>{Object.entries(months).map(([m, n]) => `${m}: ${n}`).join(' · ')}</div>
          </div>
        )}

        {ref?.external_ids && Object.keys(ref.external_ids).length > 0 && (
          <div style={styles.refSection}>
            <strong>{t('reference.externalIds')}</strong>
            {Object.entries(ref.external_ids).map(([authority, e]) => (
              <div key={authority}>
                {authority}: {e.id}{e.authorship ? ` — ${e.name || ''} ${e.authorship}` : ''}
                {e.status ? ` (${e.status})` : ''}
              </div>
            ))}
          </div>
        )}

        {internal && (
          <div style={styles.refSection} data-testid="ai-internal">
            <strong>{t('reference.internal')}</strong>
            <div>{t('reference.internalRecords', { n: internal.records })}</div>
            {Object.keys(internal.substrate || {}).length > 0 && (
              <div>{t('reference.substrate', { values: counts(internal.substrate) })}</div>
            )}
            {Object.keys(internal.hosts || {}).length > 0 && (
              <div>{t('reference.hosts', { values: counts(internal.hosts) })}</div>
            )}
            {Object.keys(internal.months || {}).length > 0 && (
              <div>{t('reference.months', { values: counts(internal.months) })}</div>
            )}
          </div>
        )}
      </div>
    </details>
  );
}

/**
 * Photographs of a candidate already held -- the bank's collections and the
 * imported reference set -- to compare the specimen against. Fetched only when
 * opened (or on mount with `open`): a result lists several candidates, and
 * loading every gallery up front would fetch pictures nobody may look at.
 */
export function ReferencePhotos({ taxon, t, open = false }) {
  const [state, setState] = useState({ status: 'idle', photos: [] });
  // The taxon a request has already gone out for, or null. A ref and not
  // state because both triggers below can fire in the same tick, and the
  // second has to see the first's decision before any re-render.
  //
  // This guard used to live INSIDE a setState updater, which read the current
  // status and fired the request from there. React treats an updater as pure
  // and may call it more than once for one update -- StrictMode does exactly
  // that in development, and both calls saw `idle`, so the gallery asked the
  // API for the same photographs twice. The test below renders in StrictMode
  // and requires one request.
  const requested = useRef(null);

  const load = useCallback(() => {
    if (requested.current === taxon) return;
    requested.current = taxon;
    setState({ status: 'loading', photos: [] });
    Promise.resolve(api.get('/photos/reference', { params: { taxon, limit: 12 } }))
      .then(res => { if (requested.current === taxon) setState({ status: 'ready', photos: res?.data?.photos || [] }); })
      .catch(() => { if (requested.current === taxon) setState({ status: 'failed', photos: [] }); });
  }, [taxon]);

  useEffect(() => {
    // A different taxon: what is on screen is no longer about it, an answer
    // still in flight for the old one is no longer wanted, and the gallery
    // may be asked again. The old `idle`-only guard made this dependency
    // dead -- a second taxon kept the first one's photographs. The one call
    // site keys on the name so it remounted instead, which is why nothing
    // showed it.
    if (requested.current !== null && requested.current !== taxon) {
      requested.current = null;
      setState({ status: 'idle', photos: [] });
    }
    if (open) load();
  }, [open, taxon, load]);
  return (
    <details style={styles.details} data-testid="ai-reference-photos" open={open || undefined}
             onToggle={e => { if (e.currentTarget.open) load(); }}>
      <summary style={styles.summary} title={t('tip.referencePhotos')}>{t('referencePhotos.title')}</summary>
      {state.status === 'loading' && <p style={styles.refNone}>{t('referencePhotos.loading')}</p>}
      {state.status === 'failed' && <p style={styles.refNone}>{t('referencePhotos.failed')}</p>}
      {state.status === 'ready' && (state.photos.length === 0
        ? <p style={styles.refNone}>{t('referencePhotos.none')}</p>
        : <PhotoGallery photos={state.photos} thumbWidth={96} thumbHeight={72} testId="ai-reference-gallery" />)}
    </details>
  );
}

/** Everything held about one name, fetched on its own. */
export function TaxonDiagnosticPanel({ name, heading, testId = 'taxon-diagnostics' }) {
  const { t } = useTranslation('ai');
  const [state, setState] = useState({ status: 'loading', data: null });

  useEffect(() => {
    if (!name) return undefined;
    let live = true;
    setState({ status: 'loading', data: null });
    Promise.resolve(api.get('/taxonomy/profile', { params: { name } }))
      .then(res => { if (live) setState({ status: 'ready', data: res?.data || null }); })
      .catch(() => { if (live) setState({ status: 'failed', data: null }); });
    return () => { live = false; };
  }, [name]);

  if (!name) return null;
  const taxon = state.data?.taxon;
  return (
    <section data-testid={testId} style={styles.panel}>
      <strong style={styles.panelTitle}>
        {heading || t('diagnostics.title')}: <em dir="ltr">{name}</em>
      </strong>
      {state.status === 'loading' && <p style={styles.refNone}>{t('diagnostics.loading')}</p>}
      {state.status === 'failed' && <p style={styles.refNone}>{t('diagnostics.failed')}</p>}
      {state.status === 'ready' && taxon && (
        <>
          {state.data.safety && <SafetyBanner safety={state.data.safety} t={t} />}
          <CandidateSafety p={taxon} t={t} />
          <ReferencePhotos taxon={name} t={t} open />
          <ReferenceProfile p={taxon} t={t} open showEmpty />
          <Sources ids={collectSourceIds([taxon.safety, taxon.reference, taxon.internal_observations])}
                   sources={state.data.sources} t={t} />
          <p style={styles.edibility}>{t('edibilityWithheld')}</p>
        </>
      )}
    </section>
  );
}

const styles = {
  panel: {
    display: 'flex', flexDirection: 'column', gap: 6, padding: 10, borderRadius: 8,
    border: '1px solid var(--c-info-soft-5)', background: 'var(--c-info-soft-4)',
  },
  panelTitle: { fontSize: 13, color: 'var(--c-info-2-fg)' },
  details: { marginTop: 6, fontSize: 12, color: 'var(--c-text-body-2-fg)' },
  summary: { cursor: 'pointer', color: 'var(--c-info-2-fg)', fontSize: 12 },
  refBody: { display: 'flex', flexDirection: 'column', gap: 4, padding: '6px 0 0' },
  refSection: { display: 'flex', flexDirection: 'column', gap: 2, marginTop: 4 },
  refNone: { margin: '6px 0 0', fontSize: 12, color: 'var(--c-text-muted-2-fg)' },
  refEmpty: { marginTop: 4, fontSize: 12, color: 'var(--c-text-muted-2-fg)', fontStyle: 'italic' },
  consistent: { padding: '6px 8px', borderRadius: 6, background: 'var(--c-primary-soft-7)', color: 'var(--c-success-4-fg)' },
  contradicted: { padding: '6px 8px', borderRadius: 6, background: 'var(--c-warning-soft-3)', color: 'var(--c-warning-text-3-fg)' },
  sourceList: { margin: '6px 0 0', paddingInlineStart: 18, display: 'flex', flexDirection: 'column', gap: 4 },
  source: { fontSize: 11, lineHeight: 1.45, overflowWrap: 'anywhere' },
  retraction: { color: 'var(--c-danger-4-fg)' },
  edibility: { margin: 0, fontSize: 12, color: 'var(--c-warning-text-7-fg)', fontWeight: 600, lineHeight: 1.45 },
};
